import React, { useEffect, useRef, useState } from "react";
import { Loader2, Lock, Upload, Trash2, Check, Globe } from "lucide-react";
import api from "../lib/api";
import * as C from "../lib/crypto";
import { encryptAndUploadWithKey } from "../lib/mediaUpload";
import { BeaconMedia } from "../pages/BeaconMedia";
import { CoachTips } from "./CoachTip";

const MAX = 1000;
const tabCls = (on) => `pb-2 font-body text-xs tracking-[0.15em] uppercase border-b-2 transition-colors ${on ? "text-white border-[#14A89A]" : "text-zinc-500 border-transparent hover:text-white"}`;
const MsgBox = ({ value, onChange, placeholder, testid }) => (
  <label className="block mb-3">
    <span className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500">Welcome message</span>
    <textarea value={value} onChange={(e) => onChange(e.target.value.slice(0, MAX))} rows={4} placeholder={placeholder} data-testid={testid}
      className="mt-1 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 font-body text-sm text-white focus:border-[#14A89A] outline-none" />
    <span className="font-body text-[11px] text-zinc-600">{value.length}/{MAX}</span>
  </label>
);

const VisitorTab = ({ form, updateForm, videoUpload }) => (
  <div data-testid="beacon-welcome-visitor-tab">
    <p className="font-body text-xs text-zinc-400 mb-3 border-l-2 border-[#14A89A] pl-2.5" data-testid="beacon-welcome-visitor-explainer"><Globe size={11} className="inline mr-1 text-[#14A89A]" /><span className="text-white font-medium">For everyone who hasn't subscribed yet.</span> A public trailer that helps visitors decide to join. Not private.</p>
    <MsgBox value={form.welcome_message || ""} onChange={(v) => updateForm({ welcome_message: v })} placeholder="Here's what you'll find inside…" testid="beacon-welcome-visitor-msg" />
    {videoUpload}
    {form.public_teaser_enabled && (
      <label className="flex items-start gap-2 mt-3 cursor-pointer" data-testid="beacon-welcome-teaser-toggle">
        <input type="checkbox" checked={!!form.welcome_teaser} onChange={(e) => updateForm({ welcome_teaser: e.target.checked })} className="accent-[#14A89A] mt-1" />
        <span className="font-body text-xs text-zinc-400"><Globe size={12} className="inline mr-1 text-zinc-500" />Also show this welcome on my public teaser page <span className="text-zinc-500">(anyone with the link can see it)</span></span>
      </label>
    )}
  </div>
);

const SubscriberTab = ({ beacon, beaconKey, ensureKey, onSaved }) => {
  const sw = beacon.sub_welcome;
  const [msg, setMsg] = useState("");
  const [video, setVideo] = useState(sw?.video ? { path: sw.video, iv: sw.video_iv } : null);
  const [file, setFile] = useState(null);
  const [reshow, setReshow] = useState(false);
  const [state, setState] = useState("idle");
  const ref = useRef();
  const touched = useRef(false);
  const editMsg = (v) => { touched.current = true; setMsg(v); };
  useEffect(() => {
    if (!beaconKey || !sw?.msg) return;
    C.decryptText(beaconKey, sw.msg, sw.msg_iv).then((p) => { if (!touched.current) setMsg(p); }).catch(() => {});
  }, [beaconKey, sw?.msg, sw?.msg_iv]);
  const save = async () => {
    const removing = !msg.trim() && !video && !file;
    if (removing && !sw) return;
    if (removing && !window.confirm("Remove your subscriber welcome? Subscribers will no longer see it.")) return;
    setState("saving");
    try {
      const key = beaconKey || await ensureKey(beacon.id);
      if (!key) throw new Error("locked");
      const body = { reshow };
      if (msg.trim()) { const e = await C.encryptText(key, msg.trim()); body.msg = e.ctB64; body.msg_iv = e.ivB64; }
      let v = video;
      if (file) { const up = await encryptAndUploadWithKey(file, (b) => C.encryptBytes(key, b), "video"); v = { path: up.path, iv: up.iv || null }; }
      if (v) { body.video = v.path; body.video_iv = v.iv; }
      await api.put("/beacon/mine/sub-welcome", body);
      setVideo(v); setFile(null); setReshow(false); setState(removing ? "removed" : "saved");
      await onSaved();
    } catch (e) { setState(e?.message === "locked" ? "locked" : "error"); }
  };
  return (
    <div data-testid="beacon-welcome-subscriber-tab">
      <p className="font-body text-xs text-zinc-400 mb-3 border-l-2 border-[#A855F7] pl-2.5" data-testid="beacon-welcome-sub-explainer"><Lock size={11} className="inline mr-1 text-[#A855F7]" /><span className="text-white font-medium">Subscribers only.</span> A private, encrypted welcome that replaces the trailer once someone subscribes.</p>
      <MsgBox value={msg} onChange={editMsg} placeholder="Welcome in, so glad you're here…" testid="beacon-welcome-sub-msg" />
      <span className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-1">Welcome video (optional)</span>
      {file ? <p className="font-body text-xs text-zinc-300 mb-2">New video ready: {file.name}</p>
        : video && <div className="mb-2"><BeaconMedia path={video.path} type="video" encKey={beaconKey} iv={video.iv} className="max-h-40 rounded" /></div>}
      <input ref={ref} type="file" accept="video/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); e.target.value = ""; }} />
      <div className="flex gap-2 mb-4">
        <button type="button" onClick={() => ref.current?.click()} data-testid="beacon-welcome-sub-video-upload" className="flex items-center gap-1.5 border border-white/15 text-zinc-300 px-3 py-1.5 font-body text-xs hover:bg-white/5"><Upload size={13} /> {video || file ? "Replace" : "Upload"}</button>
        {(video || file) && <button type="button" onClick={() => { setVideo(null); setFile(null); }} data-testid="beacon-welcome-sub-video-remove" className="flex items-center gap-1.5 border border-white/15 text-zinc-400 px-3 py-1.5 font-body text-xs hover:text-red-400"><Trash2 size={13} /> Remove</button>}
      </div>
      {sw && (
        <label className="flex items-center gap-2 mb-3 cursor-pointer" data-testid="beacon-welcome-reshow">
          <input type="checkbox" checked={reshow} onChange={(e) => setReshow(e.target.checked)} className="accent-[#14A89A]" />
          <span className="font-body text-xs text-zinc-300">Show this to all my subscribers again</span>
        </label>
      )}
      <div className="flex items-center gap-3">
        <button type="button" onClick={save} disabled={state === "saving"} data-testid="beacon-welcome-sub-save" className="flex items-center gap-1.5 bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-2 font-body text-sm hover:brightness-110 disabled:opacity-40">
          {state === "saving" ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} Save subscriber welcome
        </button>
        {state === "saved" && <span className="font-body text-xs text-[#14A89A] flex items-center gap-1" data-testid="beacon-welcome-sub-saved"><Check size={13} /> Saved</span>}
        {state === "removed" && <span className="font-body text-xs text-zinc-400" data-testid="beacon-welcome-sub-removed">Welcome removed</span>}
        {state === "error" && <span className="font-body text-xs text-red-400" data-testid="beacon-welcome-sub-error">Couldn't save, please try again.</span>}
        {state === "locked" && <span className="font-body text-xs text-amber-400">Unlock your vault first, then save.</span>}
      </div>
    </div>
  );
};

export const BeaconWelcomeEditor = ({ form, updateForm, videoUpload, beacon, beaconKey, ensureKey, onSaved }) => {
  const [tab, setTab] = useState("visitor");
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-4 mb-4" data-testid="beacon-welcome-editor">
      <div className="font-body text-sm text-white mb-1">Welcome block</div>
      <p className="font-body text-xs text-zinc-500 mb-3">Shown right under your banner. Visitors and subscribers each get their own version.</p>
      <CoachTips ids={["beacon-welcome"]} />
      <div className="flex gap-6 border-b border-white/10 mb-4">
        <button type="button" onClick={() => setTab("visitor")} className={tabCls(tab === "visitor")} data-testid="beacon-welcome-tab-visitor"><Globe size={11} className="inline mr-1" />Trailer · For everyone</button>
        <button type="button" onClick={() => setTab("subscriber")} className={tabCls(tab === "subscriber")} data-testid="beacon-welcome-tab-subscriber"><Lock size={11} className="inline mr-1" />Welcome · Subscribers only</button>
      </div>
      {tab === "visitor"
        ? <VisitorTab form={form} updateForm={updateForm} videoUpload={videoUpload} />
        : <SubscriberTab beacon={beacon} beaconKey={beaconKey} ensureKey={ensureKey} onSaved={onSaved} />}
      {beacon.handle && beacon.status === "live" && <a href={`/beacon/view/${beacon.handle}`} target="_blank" rel="noreferrer" className="inline-block mt-4 font-body text-xs text-[#14A89A] hover:underline" data-testid="beacon-welcome-preview-link">Preview on your Beacon page (switch Visitor / Subscriber there) →</a>}
    </div>
  );
};
